import { useState, useEffect, useCallback } from 'react';
import { X, ClockCounterClockwise } from '@phosphor-icons/react';
import { getApiUrl, authFetch } from '../config';
import { formatDate } from '../utils/dateUtils';

interface Props {
  source: 'cache' | 'bestand';
  id: number;
  onClose: () => void;
}

interface HistoryRow {
  id: number;
  vnm?: string;
  nnm?: string;
  plz?: string;
  ort?: string;
  optin?: string;
  optin_datetime?: string;
  hkn?: string;
  hkd?: string;
  lfr?: string;
  indate?: string;
  status?: string;
}

const PERSON_FIELDS = ['vnm', 'nnm', 'gnm', 'anr', 'ttl', 'gjj', 'gmm', 'gtt'];
const ADDRESS_FIELDS = ['str', 'hnr', 'plz', 'ort', 'lnd'];
const CONTACT_FIELDS = ['tns', 'tnc', 'hns', 'hnc', 'ema', 'telefon', 'handy_call'];
const OPTIN_FIELDS = ['optin', 'optinort', 'optincc', 'optinagent', 'optinab', 'optin_datetime', 'optinbis', 'optintel', 'optinema', 'optin_telefon', 'optin_email', 'optin_brief'];
const KAMPAGNE_FIELDS = ['kampagne', 'kampagne_typ', 'hkn', 'herkunft', 'hkd', 'lfr', 'fbd', 'fbr', 'indate', 'status', 'fc_status', 'fc_status_detail', 'fc_date', 'fc_user', 'user_tenant', 'campaign_id', 'contact_id', 'contact_status'];
const ADDRESS_CHECK_FIELDS = ['address_state', 'address_error', 'address_update', 'abuntis_verified_at', 'abuntis_adresse_ok', 'abuntis_umgezogen', 'abuntis_strasse_bad', 'abuntis_dublette', 'abuntis_dublette_phonetisch'];

const TECH_FLAG_FIELDS = ['f01','f02','f03','f04','f05','f06','f07','f08','f09','f10','f11','f12','f13','f14','f15','f16','f17','f18','f19','f20'];
const TECH_RETRIEVAL_FIELDS = ['r_lkn','r_akn','r_pcd','r_str','r_nm1','r_nm2','r_nm3','r_hnr','r_twn','r_any','r_flg'];
const TECH_INF_FIELDS = ['inf1','inf2','inf3','inf4','inf5','inf6','inf7','inf8','inf9','inf10','inf3_short'];
const TECH_MATCH_FIELDS = ['match_key','akn','alg','ald','wkz'];
const TECH_ID_FIELDS = ['aid','pid','tid','rid','inid','tblid','abs','aas','contact_id'];

function FieldRow({ label, value }: { label: string; value: any }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', padding: '0.25rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.85rem' }}>
      <div style={{ width: '160px', color: '#6b7280', fontFamily: 'monospace' }}>{label}</div>
      <div style={{ flex: 1, color: '#111827', wordBreak: 'break-word' }}>{String(value)}</div>
    </div>
  );
}

function Section({ title, data, fields }: { title: string; data: any; fields: string[] }) {
  const present = fields.filter(f => data[f] !== null && data[f] !== undefined && data[f] !== '');
  if (present.length === 0) return null;
  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ fontSize: '0.9rem', color: '#003366', margin: '0 0 0.5rem 0', borderBottom: '2px solid #e5e7eb', paddingBottom: '0.25rem' }}>{title}</h3>
      {present.map(f => <FieldRow key={f} label={f} value={data[f]} />)}
    </div>
  );
}

export default function OptinDetailDialog({ source, id, onClose }: Props) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTech, setShowTech] = useState(false);
  const [history, setHistory] = useState<HistoryRow[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setData(null);
    setError(null);
    try {
      const url = source === 'bestand'
        ? getApiUrl(`/optin-cache/bestand/${id}`)
        : getApiUrl(`/optin-cache/${id}`);
      const res = await authFetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e: any) {
      setError(e.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [source, id]);

  useEffect(() => { load(); }, [load]);

  const loadHistory = async () => {
    if (source !== 'bestand') return;
    setHistoryLoading(true);
    try {
      const res = await authFetch(getApiUrl(`/optin-cache/bestand/${id}/history`));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setHistory(json.records || []);
    } catch (e) {
      console.error('loadHistory failed:', e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: 'white', borderRadius: '8px', maxWidth: '800px', width: '100%',
          maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem', position: 'relative',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0, color: '#003366', fontSize: '1.25rem' }}>
            {source === 'bestand' ? 'Bestand-Datensatz' : 'Cache-Datensatz'} #{id}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
            aria-label="Schließen"
          >
            <X size={20} />
          </button>
        </div>

        {loading && <div style={{ color: '#6b7280' }}>Lade…</div>}
        {error && <div style={{ color: '#dc2626' }}>{error}</div>}

        {data && !loading && (
          <>
            <Section title="Person" data={data} fields={PERSON_FIELDS} />
            <Section title="Adresse" data={data} fields={ADDRESS_FIELDS} />
            <Section title="Kontakt" data={data} fields={CONTACT_FIELDS} />
            <Section title="OptIn" data={data} fields={OPTIN_FIELDS} />
            <Section title="Kampagne / Herkunft" data={data} fields={KAMPAGNE_FIELDS} />
            <Section title="Adressprüfung" data={data} fields={ADDRESS_CHECK_FIELDS} />

            <div style={{ margin: '1rem 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.85rem', color: '#374151' }}>
                <input type="checkbox" checked={showTech} onChange={e => setShowTech(e.target.checked)} />
                Alle technischen Felder anzeigen
              </label>
            </div>

            {showTech && (
              <>
                <Section title="Flags" data={data} fields={TECH_FLAG_FIELDS} />
                <Section title="Retrieval-Codes" data={data} fields={TECH_RETRIEVAL_FIELDS} />
                <Section title="INF-Felder" data={data} fields={TECH_INF_FIELDS} />
                <Section title="Matching-Keys" data={data} fields={TECH_MATCH_FIELDS} />
                <Section title="IDs" data={data} fields={TECH_ID_FIELDS} />
              </>
            )}

            {source === 'bestand' && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '2px solid #e5e7eb' }}>
                <button
                  onClick={loadHistory}
                  disabled={historyLoading || history !== null}
                  style={{
                    padding: '0.5rem 1rem', backgroundColor: '#003366', color: 'white',
                    border: 'none', borderRadius: '6px', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                  }}
                >
                  <ClockCounterClockwise size={16} />
                  {historyLoading ? 'Lade Historie…' : history !== null ? `Historie geladen (${history.length})` : 'Historie laden'}
                </button>

                {history !== null && history.length > 0 && (
                  <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb' }}>
                          <th style={{ padding: '0.4rem', textAlign: 'left' }}>Optin-Datum</th>
                          <th style={{ padding: '0.4rem', textAlign: 'left' }}>OptIn</th>
                          <th style={{ padding: '0.4rem', textAlign: 'left' }}>Quelle</th>
                          <th style={{ padding: '0.4rem', textAlign: 'left' }}>Indate</th>
                          <th style={{ padding: '0.4rem', textAlign: 'left' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map(h => (
                          <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '0.4rem' }}>{formatDate(h.optin_datetime || '')}</td>
                            <td style={{ padding: '0.4rem' }}>{h.optin || '-'}</td>
                            <td style={{ padding: '0.4rem' }}>{h.hkn || '-'} / {h.lfr || '-'}</td>
                            <td style={{ padding: '0.4rem' }}>{h.indate || '-'}</td>
                            <td style={{ padding: '0.4rem' }}>{h.status || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {history !== null && history.length === 0 && (
                  <div style={{ marginTop: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>
                    Keine Historie gefunden.
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
